import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ImportWizard from "./ImportWizard";
import TransferHistory from "./TransferHistory";
import AssignmentHistory from "./AssignmentHistory";
import BackupRestore from "./BackupRestore";
import RoleEditor from "./RoleEditor";
import UserMindMapKnowledge from "./UserMindMapKnowledge";
import ChartSettings from "./ChartSettings";
import PageHeader from "./PageHeader";
import { API_URL } from "../config";
import { useRolePermissions } from '../hooks/useRolePermissions';

// Modal pro změnu hesla
function PasswordChangeModal({ user, onSave, onClose, isDarkMode }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      alert("Heslo nesmí být prázdné");
      return;
    }
    
    if (password !== confirmPassword) {
      alert("Hesla se neshodují");
      return;
    }
    
    if (password.length < 4) {
      alert("Heslo musí mít alespoň 4 znaky");
      return;
    }
    
    await onSave(password);
  };

  return (
    <div style={{
      position: "fixed", left: 0, top: 0, width: "100vw", height: "100vh",
      background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
    }}>
      <form
        onSubmit={handleSubmit}
        className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-6 rounded-lg shadow-lg min-w-[350px] max-w-md`}
      >
        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : ''}`}>
          🔐 Změna hesla pro uživatele: {user.name}
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nové heslo:</label>
            <input
              type="password"
              className={`w-full border px-3 py-2 rounded ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'}`}
              placeholder="Zadejte nové heslo"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoFocus
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Potvrdit heslo:</label>
            <input
              type="password"
              className={`w-full border px-3 py-2 rounded ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'}`}
              placeholder="Zadejte heslo znovu"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
            />
          </div>
        </div>
        
        <div className="flex gap-2 justify-end mt-6">
          <button
            type="button"
            className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
            onClick={onClose}
          >
            Zrušit
          </button>
          <button
            type="submit"
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white"
          >
            Změnit heslo
          </button>
        </div>
      </form>
    </div>
  );
}

function UserModal({ user, onSave, onClose, isDarkMode }) {
  const [form, setForm] = useState(
    user || { name: "", email: "", phone: "", office: "", role: "user", vfLogin: "", password: "" }
  );
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);

  // Načíst dostupné role
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const response = await fetch(`${API_URL}/roles`);
        if (response.ok) {
          const data = await response.json();
          // Filtrovat skryté role (jako 'database')
          const visibleRoles = data.filter(role => !role.hidden);
          setRoles(visibleRoles);
        } else {
          // Fallback na výchozí role
          setRoles([
            { name: 'user', displayName: 'Uživatel' },
            { name: 'admin', displayName: 'Administrator' },
            { name: 'obchodnik', displayName: 'Obchodní zástupce' },
            { name: 'podpora', displayName: 'Podpora' },
            { name: 'callcentrum', displayName: 'Callcentrum' },
            { name: 'test', displayName: 'Testovací účet' }
          ]);
        }
      } catch (error) {
        console.error('Chyba při načítání rolí:', error);
        // Fallback na výchozí role
        setRoles([
          { name: 'user', displayName: 'Uživatel' },
          { name: 'admin', displayName: 'Administrator' },
          { name: 'obchodnik', displayName: 'Obchodní zástupce' },
          { name: 'podpora', displayName: 'Podpora' },
          { name: 'callcentrum', displayName: 'Callcentrum' },
          { name: 'test', displayName: 'Testovací účet' }
        ]);
      } finally {
        setLoadingRoles(false);
      }
    };
    loadRoles();
  }, []);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    
    // Validace
    if (!form.name.trim()) {
      alert("Jméno je povinné");
      return;
    }
    if (!form.email.trim()) {
      alert("E-mail je povinný");
      return;
    }
    if (!user && !form.password.trim()) {
      alert("Heslo je povinné pro nového uživatele");
      return;
    }
    
    try {
      await onSave(form);
    } catch (error) {
      console.error('Chyba při ukládání:', error);
    }
  };

  return (
    <div style={{
      position: "fixed", left: 0, top: 0, width: "100vw", height: "100vh",
      background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
    }}>
      <form
        onSubmit={handleSubmit}
        className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-6 rounded shadow min-w-[320px] flex flex-col gap-4`}
        style={{ minWidth: 320, maxWidth: 400 }}
      >
        <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : ''}`}>
          {user ? "Upravit uživatele" : "Nový uživatel"}
        </h3>
        <input
          className={`border px-3 py-2 rounded ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white'}`}
          name="name"
          placeholder="Jméno"
          value={form.name}
          onChange={handleChange}
          required
        />
        <input
          className={`border px-3 py-2 rounded ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white'}`}
          name="email"
          placeholder="E-mail"
          value={form.email}
          onChange={handleChange}
          required
          type="email"
        />
        <input
          className={`border px-3 py-2 rounded ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white'}`}
          name="phone"
          placeholder="Telefon"
          value={form.phone}
          onChange={handleChange}
          type="tel"
        />
        <input
          className={`border px-3 py-2 rounded ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white'}`}
          name="office"
          placeholder="Kancelář / Lokalita"
          value={form.office}
          onChange={handleChange}
          type="text"
        />
        <input
          className={`border px-3 py-2 rounded ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white'}`}
          name="vfLogin"
          placeholder="VF Login (např. xkovac01)"
          value={form.vfLogin || ''}
          onChange={handleChange}
          type="text"
        />
        <select
          className={`border px-3 py-2 rounded ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white'}`}
          name="role"
          value={form.role}
          onChange={handleChange}
          disabled={loadingRoles}
        >
          {loadingRoles ? (
            <option>Načítám role...</option>
          ) : (
            roles.map(role => (
              <option key={role.name} value={role.name}>
                {role.displayName}
              </option>
            ))
          )}
        </select>
        {!user && (
          <input
            className={`border px-3 py-2 rounded ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white'}`}
            name="password"
            placeholder="Heslo"
            value={form.password}
            onChange={handleChange}
            required
            type="password"
          />
        )}
        <div className="flex gap-2 justify-end mt-2">
          <button
            type="button"
            className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
            onClick={onClose}
          >
            Zrušit
          </button>
          <button
            type="submit"
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white"
          >
            Uložit
          </button>
        </div>
      </form>
    </div>
  );
}

function TeamleaderModal({ user, allUsers, onSave, onClose, isDarkMode }) {
  const [selectedUserIds, setSelectedUserIds] = useState(user.teamleaderFor || []);

  const handleToggleUser = (userId) => {
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    } else {
      setSelectedUserIds([...selectedUserIds, userId]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(user._id, selectedUserIds);
  };

  // Filtrovat uživatele - nezobrazovat sám sebe
  const availableUsers = allUsers.filter(u => u._id !== user._id);

  return (
    <div style={{
      position: "fixed", left: 0, top: 0, width: "100vw", height: "100vh",
      background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
    }}>
      <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-6 rounded shadow max-w-lg w-full mx-4`}>
        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : ''}`}>
          👥 Teamleader přiřazení pro: {user.name}
        </h3>
        <p className={`mb-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Vyberte obchodníky, kteří budou přiřazeni jako podřízení pro tohoto teamleadera:
        </p>
        
        <form onSubmit={handleSubmit}>
          <div className={`mb-4 p-3 rounded max-h-80 overflow-y-auto ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            {availableUsers.length === 0 ? (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Žádní další uživatelé k dispozici
              </p>
            ) : (
              <div className="space-y-2">
                {availableUsers.map(u => (
                  <label
                    key={u._id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                      isDarkMode 
                        ? 'hover:bg-gray-600' 
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(u._id)}
                      onChange={() => handleToggleUser(u._id)}
                      className="w-4 h-4"
                    />
                    <span className="flex-1">
                      {u.name}
                      {u.email && (
                        <span className={`ml-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          ({u.email})
                        </span>
                      )}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      u.role === 'admin' 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {u.role}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className={`mb-4 p-2 rounded text-sm ${isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-50 text-blue-800'}`}>
            💡 Vybráno: <strong>{selectedUserIds.length}</strong> {selectedUserIds.length === 1 ? 'uživatel' : selectedUserIds.length < 5 ? 'uživatelé' : 'uživatelů'}
          </div>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
              onClick={onClose}
            >
              Zrušit
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded bg-purple-600 hover:bg-purple-700 text-white"
            >
              💾 Uložit přiřazení
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function UsersAdmin({ isDarkMode, companies = [], userId, userRole, onRestore, currentUserId }) {
  const navigate = useNavigate();
  const { hasPermission, permissions, loading: roleLoading } = useRolePermissions(userRole);
  
  const [users, setUsers] = useState([]);
  const [modalUser, setModalUser] = useState(null); // null = zavřeno, {} = nový, user = editace
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [importUpdateMode, setImportUpdateMode] = useState(false);
  const [companiesData, setCompaniesData] = useState([]);
  const [userContactCounts, setUserContactCounts] = useState({});
  const [showDeleteContactsModal, setShowDeleteContactsModal] = useState(false);
  const [selectedUserForDeletion, setSelectedUserForDeletion] = useState(null);
  const [showTransferHistory, setShowTransferHistory] = useState(false);
  const [showAssignmentHistory, setShowAssignmentHistory] = useState(false);
  const [showTeamleaderModal, setShowTeamleaderModal] = useState(false);
  const [selectedUserForTeamleader, setSelectedUserForTeamleader] = useState(null);
  const [showBackupRestore, setShowBackupRestore] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showRoleEditor, setShowRoleEditor] = useState(false);
  const [showChartSettings, setShowChartSettings] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [showUserKnowledge, setShowUserKnowledge] = useState(false);
  const [selectedUserForKnowledge, setSelectedUserForKnowledge] = useState(null);
  const [aiData, setAiData] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiModalMode, setAiModalMode] = useState('data');
  const [aiOfferData, setAiOfferData] = useState('');
  const [aiOfferPrompt, setAiOfferPrompt] = useState('');
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [showCommissionCoef, setShowCommissionCoef] = useState(false);
  const [commissionCoef, setCommissionCoef] = useState({ coefMin: 4, coefMax: 6 });
  const [coefSaving, setCoefSaving] = useState(false);
  const [coefMin, setCoefMin] = useState('');
  const [coefMax, setCoefMax] = useState('');
  const [coefSingle, setCoefSingle] = useState(false); // true = jen jedna hodnota

  // Načti uživatele
  // Načti uživatele a spusť interval pro online status pouze jednou při mountu
  useEffect(() => {
    loadUsers();
    loadOnlineUsers();
    // Načti koeficient pro očekávanou provizi
    fetch(`${API_URL}/commission-coef?userId=${localStorage.getItem('lastUserId') || ''}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setCommissionCoef(data);
          if (data.coefMax == null) {
            setCoefSingle(true);
            setCoefMin(String(data.coefMin ?? 4));
            setCoefMax('');
          } else {
            setCoefSingle(false);
            setCoefMin(String(data.coefMin ?? 4));
            setCoefMax(String(data.coefMax ?? 6));
          }
        }
      })
      .catch(() => {});

    const interval = setInterval(() => {
      loadOnlineUsers();
    }, 30000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aktualizuj data firem když se změní companies prop (bez volání loadUsers)
  useEffect(() => {
    if (companies && companies.length > 0) {
      setCompaniesData(companies);
    }
  }, [companies]);

  // Prepočítej counts když se změní data - s debouncing pro výkon
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (users.length > 0 && companiesData.length > 0) {
        calculateContactCounts(users, companiesData);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [users, companiesData]);

  const loadUsers = async () => {
    try {
      console.log('Loading users...');
      const res = await fetch(`${API_URL}/users?userId=${currentUserId || ''}`);
      if (res.ok) {
        const data = await res.json();
        console.log('Users loaded successfully:', data.length, 'users');
        if (data.length > 0) {
          console.log('Sample user structure:', data[0]);
        }
        setUsers(data);
      } else {
        console.error('Failed to load users:', res.status);
        setUsers([]);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setUsers([]);
    }
  };

  const loadOnlineUsers = async () => {
    try {
      const res = await fetch("https://crm-backend-6jqe.onrender.com/api/users/online");
      if (res.ok) {
        const onlineUserIds = await res.json();
        setOnlineUsers(new Set(onlineUserIds));
        console.log('🟢 Online users:', onlineUserIds.length);
      }
    } catch (err) {
      console.error('Error loading online users:', err);
    }
  };

  const loadCompaniesData = async () => {
    try {
      console.log('Loading companies data for user counts...');
      const res = await fetch("https://crm-backend-6jqe.onrender.com/api/companies");
      if (res.ok) {
        const data = await res.json();
        console.log('Companies loaded successfully:', data.length, 'companies');
        if (data.length > 0) {
          console.log('Sample company structure:', data[0]);
        }
        setCompaniesData(data);
      } else {
        console.error('Failed to load companies:', res.status);
        setCompaniesData([]);
      }
    } catch (err) {
      console.error('Error loading companies:', err);
      setCompaniesData([]);
    }
  };

  const calculateContactCounts = (usersData, companiesDataParam) => {
    if (!usersData || !companiesDataParam) {
      console.log('Missing data for contact counts calculation');
      return;
    }
    
    console.log('Calculating contact counts...');
    console.log('Users data:', usersData.length, 'users');
    console.log('Companies data:', companiesDataParam.length, 'companies');
    
    const counts = {};
    usersData.forEach(user => {
      // Zkusit různé možnosti jak je uloženo userId v company
      const userCompanies = companiesDataParam.filter(company => {
        // Kontrola různých způsobů jak může být uživatel připojen k firmě
        // createdBy = aktuální vlastník (i když název je matoučí)
        const matchesCreatedBy = 
          company.createdBy === user._id ||
          (typeof company.createdBy === 'object' && company.createdBy?._id === user._id);
        
        const matchesOperator = 
          (company.operator && user.name && company.operator.toLowerCase() === user.name.toLowerCase()) ||
          (company.operator && user.email && company.operator.toLowerCase() === user.email.toLowerCase()) ||
          (company.operator && user.username && company.operator.toLowerCase() === user.username.toLowerCase());
        
        // Přidáno logování pro debug
        const matches = matchesCreatedBy || matchesOperator;
        
        if (matches && usersData.length <= 10) { // Debug pouze pro malý počet uživatelů
          console.log(`User ${user.name} matches company ${company.name} via`, {
            matchesCreatedBy,
            matchesOperator,
            createdBy: company.createdBy,
            operator: company.operator,
            userEmail: user.email,
            userName: user.name,
            userUsername: user.username
          });
        }
        
        return matches;
      });
      
      counts[user._id] = userCompanies.length;
      console.log(`User ${user.name}: ${userCompanies.length} companies`);
    });
    
    console.log('Final counts:', counts);
    setUserContactCounts(counts);
  };

  // Callback po úspěšném importu
  const handleImportComplete = () => {
    loadUsers(); // Obnovit seznam uživatelů
    // companies se aktualizují automaticky přes prop z App.js
    setShowImportWizard(false);
    setImportUpdateMode(false);
  };

  // Smazání kontaktů konkrétního uživatele
  const handleDeleteUserContacts = async (user) => {
    // createdBy = aktuální vlastník (i když název je matoučí)
    const userCompanies = companiesData.filter(company => 
      company.createdBy === user._id || company.createdBy?._id === user._id
    );
    
    if (userCompanies.length === 0) {
      alert("Tento uživatel nemá žádné kontakty/firmy.");
      return;
    }

    setSelectedUserForDeletion({ user, companies: userCompanies });
    setShowDeleteContactsModal(true);
  };

  const confirmDeleteUserContacts = async () => {
    if (!selectedUserForDeletion) return;

    try {
      // Smaž všechny firmy uživatele
      for (const company of selectedUserForDeletion.companies) {
        await fetch(`https://crm-backend-6jqe.onrender.com/api/companies/${company._id}`, {
          method: "DELETE",
        });
      }

      alert(`Bylo smazáno ${selectedUserForDeletion.companies.length} kontaktů/firem uživatele ${selectedUserForDeletion.user.name}.`);
      
      // companies se aktualizují automaticky přes prop z App.js
      setShowDeleteContactsModal(false);
      setSelectedUserForDeletion(null);
    } catch (err) {
      alert("Chyba při mazání kontaktů.");
      console.error('Chyba při mazání kontaktů:', err);
    }
  };

  // Přepnutí na uživatele (login jako jiný uživatel)
  const handleSwitchToUser = (user) => {
    if (window.confirm(`Opravdu se chcete přihlásit jako uživatel "${user.name}"?`)) {
      // Nastavit nové údaje do localStorage
      localStorage.setItem("userId", user._id);
      localStorage.setItem("name", user.name);
      localStorage.setItem("role", user.role || "user");
      
      // Přesměrovat na hlavní stránku
      window.location.href = "/";
    }
  };

  // Smazání uživatele
  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Opravdu chcete smazat tohoto uživatele? Tato akce je nevratná!")) return;
    const res = await fetch(`https://crm-backend-6jqe.onrender.com/api/users/${userId}`, {
      method: "DELETE",
    });
    if (res.ok) {
      alert("Uživatel byl smazán.");
      setUsers(users.filter(u => u._id !== userId));
    } else {
      const data = await res.json();
      alert(data.message || "Uživatele nelze smazat (pravděpodobně má přiřazené firmy/kontakty).");
    }
  };

  // Formátování Last Online
  const formatLastOnline = (lastOnline) => {
    if (!lastOnline) return 'Nikdy';
    const now = new Date();
    const lastOnlineDate = new Date(lastOnline);
    const diffMs = now - lastOnlineDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 5) return 'Právě teď';
    if (diffMins < 60) return `Před ${diffMins} min`;
    if (diffHours < 24) return `Před ${diffHours} h`;
    if (diffDays === 1) return 'Včera';
    if (diffDays < 7) return `Před ${diffDays} dny`;
    return lastOnlineDate.toLocaleDateString('cs-CZ');
  };

  // Funkce pro dropdown menu
  const toggleDropdown = (userId) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const closeAllDropdowns = () => {
    setOpenDropdowns({});
  };

  // Změna hesla uživatele
  const handleChangePassword = (user) => {
    setSelectedUserForPassword(user);
    setShowPasswordModal(true);
    closeAllDropdowns();
  };

  // Otevření teamleader modalu
  const handleOpenTeamleaderModal = (user) => {
    setSelectedUserForTeamleader(user);
    setShowTeamleaderModal(true);
    closeAllDropdowns();
  };

  // Otevření AI modalu
  const handleOpenAIModal = async () => {
    try {
      const uid = currentUserId || '';
      const dataRes = await fetch(`${API_URL}/ai-data?userId=${uid}`);
      if (dataRes.ok) {
        const dataResponse = await dataRes.json();
        setAiData(dataResponse.data || '');
      }
      const promptRes = await fetch(`${API_URL}/ai-prompt?userId=${uid}`);
      if (promptRes.ok) {
        const promptData = await promptRes.json();
        setAiPrompt(promptData.prompt || '');
      }
      const offerRes = await fetch(`${API_URL}/ai-offer-config?userId=${uid}`);
      if (offerRes.ok) {
        const offerData = await offerRes.json();
        setAiOfferData(offerData.data || '');
        setAiOfferPrompt(offerData.prompt || '');
      }
    } catch (error) {
      console.error('Error loading AI data/prompt:', error);
    }
    setShowAIModal(true);
  };

  // Toggle 2FA pro uživatele
  const handleToggle2FA = async (user) => {
    const newEnabled = !user.twoFactorEnabled;
    try {
      const res = await fetch(`https://crm-backend-6jqe.onrender.com/api/users/${user._id}/toggle-2fa`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: newEnabled }),
      });
      if (res.ok) {
        const { user: updatedUser } = await res.json();
        setUsers(users.map(u => u._id === updatedUser._id ? updatedUser : u));
      } else {
        const data = await res.json();
        alert(data.error || "Chyba při změně 2FA nastavení");
      }
    } catch (err) {
      console.error("Chyba při toggle 2FA:", err);
      alert("Chyba při komunikaci se serverem");
    }
  };

  // Uložení teamleader přiřazení
  const handleSaveTeamleaderAssignments = async (userId, selectedUserIds) => {
    try {
      const res = await fetch(`https://crm-backend-6jqe.onrender.com/api/users/${userId}/teamleader`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ teamleaderFor: selectedUserIds }),
      });

      if (res.ok) {
        const updatedUser = await res.json();
        // Aktualizuj seznam uživatelů
        setUsers(users.map(u => u._id === updatedUser._id ? updatedUser : u));
        setShowTeamleaderModal(false);
        setSelectedUserForTeamleader(null);
        alert(`Teamleader přiřazení pro uživatele "${updatedUser.name}" byla úspěšně uložena.`);
      } else {
        const error = await res.json();
        alert(`Chyba při ukládání: ${error.message || 'Neznámá chyba'}`);
      }
    } catch (err) {
      console.error('Chyba při ukládání teamleader přiřazení:', err);
      alert(`Chyba při komunikaci se serverem: ${err.message}`);
    }
  };

  // Uložení nového hesla
  const handleSavePassword = async (password) => {
    if (!selectedUserForPassword) return;
    
    try {
      const res = await fetch(`https://crm-backend-6jqe.onrender.com/api/users/${selectedUserForPassword._id}/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      if (res.ok) {
        alert(`Heslo pro uživatele "${selectedUserForPassword.name}" bylo úspěšně změněno.`);
        setShowPasswordModal(false);
        setSelectedUserForPassword(null);
      } else {
        const error = await res.json();
        alert(`Chyba při změně hesla: ${error.message || 'Neznámá chyba'}`);
      }
    } catch (err) {
      console.error('Chyba při změně hesla:', err);
      alert(`Chyba při komunikaci se serverem: ${err.message}`);
    }
  };

  // Uložení AI dat
  const handleSaveAIData = async () => {
    try {
      if (aiModalMode === 'data') {
        const response = await fetch(`${API_URL}/ai-data`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: aiData })
        });
        if (response.ok) { setShowAIModal(false); alert('AI data byla úspěšně uložena!'); }
        else { alert('Chyba při ukládání AI dat.'); }
      } else if (aiModalMode === 'prompt') {
        const response = await fetch(`${API_URL}/ai-prompt`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt: aiPrompt })
        });
        if (response.ok) { setShowAIModal(false); alert('AI prompt byl úspěšně uložen!'); }
        else { alert('Chyba při ukládání AI promptu.'); }
      } else if (aiModalMode === 'offer-data') {
        const response = await fetch(`${API_URL}/ai-offer-config`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: aiOfferData })
        });
        if (response.ok) { setShowAIModal(false); alert('Data pro "Co nabídnout?" byla uložena!'); }
        else { alert('Chyba při ukládání.'); }
      } else if (aiModalMode === 'offer-prompt') {
        const response = await fetch(`${API_URL}/ai-offer-config`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt: aiOfferPrompt })
        });
        if (response.ok) { setShowAIModal(false); alert('Prompt pro "Co nabídnout?" byl uložen!'); }
        else { alert('Chyba při ukládání.'); }
      }
    } catch (error) {
      console.error('Error saving AI data/prompt:', error);
      alert('Chyba při ukládání.');
    }
  };

  // Úprava uživatele - otevření modálního okna pro editaci
  const handleEditUser = (user) => setModalUser(user);

  // Přidání nového uživatele - otevření prázdného formuláře
  const handleCreateUser = () => setModalUser({});

  // Uložení uživatele (nového nebo upraveného)
  const handleSaveUser = async (form) => {
    try {
      if (form._id) {
        // Editace existujícího uživatele
        const res = await fetch(`https://crm-backend-6jqe.onrender.com/api/users/${form._id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        
        if (res.ok) {
          const updated = await res.json();
          setUsers(users.map(u => u._id === updated._id ? updated : u));
          setModalUser(null);
          alert("Uživatel byl úspěšně upraven.");
        } else {
          const error = await res.json();
          alert(`Chyba při ukládání uživatele: ${error.message || 'Neznámá chyba'}`);
        }
      } else {
        // Nový uživatel - použijeme stejný endpoint jako pro registraci
        const userData = {
          name: form.name,
          username: form.email, // Backend možná očekává username místo email
          email: form.email,
          phone: form.phone,
          office: form.office,
          password: form.password,
          role: form.role || 'user',
          userId: currentUserId // pro tenant izolaci
        };
        
        const res = await fetch(`${API_URL}/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(userData),
        });
        
        if (res.ok) {
          // Obnovíme seznam uživatelů se správným tenantId filtrem
          const usersRes = await fetch(`${API_URL}/users?userId=${currentUserId || ''}`);
          const updatedUsers = await usersRes.json();
          setUsers(updatedUsers);
          setModalUser(null);
          alert("Nový uživatel byl úspěšně vytvořen.");
        } else {
          const error = await res.json();
          alert(`Chyba při vytváření uživatele: ${error.message || 'Neznámá chyba'}`);
        }
      }
    } catch (err) {
      console.error('Chyba při komunikaci se serverem:', err);
      alert(`Chyba při komunikaci se serverem: ${err.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-blue-100 dark:from-gray-800 dark:to-gray-900 py-10">
      <div className="w-[98vw] max-w-[1800px] mx-auto px-4">
        
        {/* Hlavička */}
        <PageHeader
          title="Správa uživatelů"
          actions={
            <>
              <button
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold shadow-md transition"
                onClick={handleCreateUser}
              >
                Nový uživatel
              </button>
              <button
                onClick={() => navigate('/contact-management')}
                className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold shadow-md transition"
              >
                📋 Správa kontaktů
              </button>
              <button
                onClick={() => setShowTransferHistory(true)}
                className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-semibold shadow-md transition"
              >
                📊 Historie přesunů
              </button>
              <button
                onClick={() => setShowAssignmentHistory(true)}
                className="px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold shadow-md transition"
              >
                📥 Historie přidělování
              </button>
              <button
                onClick={() => {
                  navigate('/status-editor');
                }}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-md transition"
              >
                🎨️ Editor stavů
              </button>
              <button
                onClick={handleOpenAIModal}
                className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold shadow-md transition"
              >
                🤖 AI
              </button>
              <button
                onClick={() => {
                  setShowCommissionCoef(true);
                }}
                className="px-4 py-2 rounded-lg bg-green-700 hover:bg-green-800 text-white font-semibold shadow-md transition"
              >
                💰 Koeficient provize
              </button>
              <button
                onClick={() => setShowChartSettings(true)}
                className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-semibold shadow-md transition"
              >
                📊 Nastavení grafů
              </button>
              <button
                onClick={() => setShowRoleEditor(true)}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-md transition"
              >
                🎭 Role
              </button>
              <button
                onClick={() => navigate('/batch-visibility')}
                className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-semibold shadow-md transition"
              >
                👁️ Přidělování kontaktů
              </button>
              <button
                onClick={() => navigate('/knowledge')}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md transition"
              >
                🖥️ Správa znalostí
              </button>
              <button
                onClick={() => navigate('/callcenter-admin')}
                className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold shadow-md transition"
              >
                📞 Callcentrum
              </button>
              <button
                onClick={() => navigate('/welcome-admin')}
                className="px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold shadow-md transition"
              >
                👋 Uvítání
              </button>
              <button
                onClick={() => setShowBackupRestore(true)}
                className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-semibold shadow-md transition"
              >
                💾 Záloha a obnovení
              </button>
              <button
                onClick={() => navigate('/restore-backup')}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-md transition"
              >
                ♻️ Obnova ze zálohy
              </button>
              <button
                onClick={() => navigate('/superusery')}
                className="px-4 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white font-semibold shadow-md transition"
              >
                🔑 VF Loginy
              </button>
              <button
                onClick={() => navigate('/uvolneni-mustr')}
                className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold shadow-md transition"
              >
                ✉️ Mustr uvolnění
              </button>
              <button
                onClick={() => navigate('/suppliers')}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-md transition"
              >
                🤝 Dodavatelé
              </button>
              <button
                onClick={() => navigate('/backoffice')}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-md transition"
              >
                🎧 Backoffice
              </button>
              <button
                onClick={() => navigate('/lead-ba')}
                className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-semibold shadow-md transition"
              >
                🎯 Lead BA
              </button>
            </>
          }
        />
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded shadow">
          <div className="overflow-x-auto rounded-lg shadow">
        <table className={`min-w-full rounded shadow mb-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <thead>
            <tr className={`${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-blue-100 text-blue-900'}`}>
              <th className="py-3 px-4 text-left font-semibold">Jméno</th>
              <th className="py-3 px-4 text-left font-semibold">VF Login</th>
              <th className="py-3 px-4 text-left font-semibold">E-mail</th>
              <th className="py-3 px-4 text-left font-semibold">Telefon</th>
              <th className="py-3 px-4 text-left font-semibold">Kancelář</th>
              <th className="py-3 px-4 text-left font-semibold">Role</th>
              <th className="py-3 px-4 text-left font-semibold">Naposledy online</th>
              <th className="py-3 px-4 text-left font-semibold w-16">2FA</th>
              <th className="py-3 px-4 text-left font-semibold w-24">Akce</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user._id} className={`${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                <td className={`py-3 px-4 whitespace-nowrap ${isDarkMode ? 'text-gray-200' : ''}`}>{user.name}</td>
                <td className={`py-3 px-4 whitespace-nowrap ${isDarkMode ? 'text-gray-200' : ''}`}>{user.vfLogin || "-"}</td>
                <td className={`py-3 px-4 whitespace-nowrap ${isDarkMode ? 'text-gray-200' : ''}`}>{user.email}</td>
                <td className={`py-3 px-4 whitespace-nowrap ${isDarkMode ? 'text-gray-200' : ''}`}>{user.phone || "-"}</td>
                <td className={`py-3 px-4 whitespace-nowrap ${isDarkMode ? 'text-gray-200' : ''}`}>{user.office || "-"}</td>
                <td className="py-3 px-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    user.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className={`py-3 px-4 whitespace-nowrap ${isDarkMode ? 'text-gray-200' : ''}`}>
                  <span 
                    className={`text-xs px-2 py-1 rounded inline-flex items-center gap-1 ${
                      onlineUsers.has(user._id)
                        ? (isDarkMode ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800')
                        : (isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500')
                    }`}
                    title={user.lastOnline ? `Poslední aktivita: ${new Date(user.lastOnline).toLocaleString('cs-CZ')}` : 'Uživatel se ještě nikdy nepřihlásil'}
                  >
                    {onlineUsers.has(user._id) && (
                      <span className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full inline-block"></span>
                    )}
                    {onlineUsers.has(user._id) ? 'Online' : formatLastOnline(user.lastOnline)}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <button
                    onClick={() => handleToggle2FA(user)}
                    title={user.twoFactorEnabled ? '2FA zapnuto – kliknutím vypnout' : '2FA vypnuto – kliknutím zapnout'}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                      user.twoFactorEnabled ? 'bg-blue-600' : (isDarkMode ? 'bg-gray-600' : 'bg-gray-300')
                    }`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                      user.twoFactorEnabled ? 'translate-x-4' : 'translate-x-1'
                    }`} />
                  </button>
                </td>
                <td className="py-3 px-4 relative">
                  <div className="flex justify-center">
                    <button
                      className={`px-3 py-1 rounded ${isDarkMode ? 'hover:bg-gray-700 bg-gray-600' : 'hover:bg-gray-100 bg-gray-50'} transition-colors border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}
                      onClick={() => toggleDropdown(user._id)}
                      title="Akce pro uživatele"
                    >
                      <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                    
                    {/* Dropdown menu */}
                    {openDropdowns[user._id] && (
                      <div className={`absolute right-4 z-50 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-lg py-1 min-w-[220px] top-12`}>
                        <button
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50'} transition-colors flex items-center gap-2`}
                          onClick={() => {
                            handleEditUser(user);
                            closeAllDropdowns();
                          }}
                        >
                          ✏️ Upravit uživatele
                        </button>
                        <button
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50'} transition-colors flex items-center gap-2`}
                          onClick={() => {
                            handleOpenTeamleaderModal(user);
                          }}
                        >
                          👥 Teamleader
                        </button>
                        <button
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50'} transition-colors flex items-center gap-2`}
                          onClick={() => {
                            handleSwitchToUser(user);
                            closeAllDropdowns();
                          }}
                        >
                          👤 Přepnout na uživatele
                        </button>
                        <button
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50'} transition-colors flex items-center gap-2`}
                          onClick={() => {
                            handleChangePassword(user);
                          }}
                        >
                          🔐 Změnit heslo
                        </button>
                        <button
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-gray-700 text-blue-300' : 'hover:bg-gray-50 text-blue-600'} transition-colors flex items-center gap-2`}
                          onClick={() => {
                            setSelectedUserForKnowledge(user);
                            setShowUserKnowledge(true);
                            closeAllDropdowns();
                          }}
                        >
                          🖥️ Správa znalostí
                        </button>
                        <hr className={`my-1 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />
                        <button
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-gray-700 text-orange-300' : 'hover:bg-gray-50 text-orange-600'} transition-colors flex items-center gap-2`}
                          onClick={() => {
                            handleDeleteUserContacts(user);
                            closeAllDropdowns();
                          }}
                        >
                          🗑️ Smazat kontakty
                        </button>
                        <button
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-gray-700 text-red-300' : 'hover:bg-gray-50 text-red-600'} transition-colors flex items-center gap-2`}
                          onClick={() => {
                            handleDeleteUser(user._id);
                            closeAllDropdowns();
                          }}
                        >
                          ❌ Smazat uživatele
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {modalUser !== null && (
        <UserModal
          user={modalUser && modalUser._id ? modalUser : null}
          onSave={handleSaveUser}
          onClose={() => setModalUser(null)}
          isDarkMode={isDarkMode}
        />
      )}
      
      {showImportWizard && (
        <ImportWizard
          onImportComplete={handleImportComplete}
          onClose={() => {
            setShowImportWizard(false);
            setImportUpdateMode(false);
          }}
          updateMode={importUpdateMode}
          userId={currentUserId}
        />
      )}

      {/* Modal pro potvrzení smazání kontaktů */}
      {showDeleteContactsModal && selectedUserForDeletion && (
        <div style={{
          position: "fixed", left: 0, top: 0, width: "100vw", height: "100vh",
          background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div className={`p-6 rounded shadow max-w-md w-full mx-4 ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'}`}>
            <h3 className={`text-lg font-bold mb-4 text-red-${isDarkMode ? '400' : '600'}`}>⚠️ Potvrzení smazání kontaktů</h3>
            <p className="mb-4">
              Opravdu chcete smazat všechny kontakty/firmy uživatele <strong>{selectedUserForDeletion.user.name}</strong>?
            </p>
            <div className={`mb-4 p-3 rounded max-h-40 overflow-y-auto ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <p className="font-semibold mb-2">Kontakty ke smazání ({selectedUserForDeletion.companies.length}):</p>
              <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {selectedUserForDeletion.companies.slice(0, 10).map((company, index) => (
                  <li key={company._id}>
                    {index + 1}. {company.name} ({company.ico})
                  </li>
                ))}
                {selectedUserForDeletion.companies.length > 10 && (
                  <li className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>... a dalších {selectedUserForDeletion.companies.length - 10} kontaktů</li>
                )}
              </ul>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                onClick={() => {
                  setShowDeleteContactsModal(false);
                  setSelectedUserForDeletion(null);
                }}
              >
                Zrušit
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmDeleteUserContacts}
              >
                Smazat kontakty
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal pro historii přesunů */}
      {showTransferHistory && (
        <TransferHistory
          isDarkMode={isDarkMode}
          onClose={() => setShowTransferHistory(false)}
        />
      )}

      {/* Modal pro historii přidělování */}
      {showAssignmentHistory && (
        <AssignmentHistory
          isDarkMode={isDarkMode}
          onClose={() => setShowAssignmentHistory(false)}
          userId={currentUserId}
        />
      )}

      {/* Modal pro nastavení teamleader přiřazení */}
      {showTeamleaderModal && selectedUserForTeamleader && (
        <TeamleaderModal
          user={selectedUserForTeamleader}
          allUsers={users}
          onSave={handleSaveTeamleaderAssignments}
          onClose={() => {
            setShowTeamleaderModal(false);
            setSelectedUserForTeamleader(null);
          }}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Modal pro změnu hesla */}
      {showPasswordModal && selectedUserForPassword && (
        <PasswordChangeModal
          user={selectedUserForPassword}
          onSave={handleSavePassword}
          onClose={() => {
            setShowPasswordModal(false);
            setSelectedUserForPassword(null);
          }}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Modal pro správu znalostí uživatele */}
      {showUserKnowledge && selectedUserForKnowledge && (
        <UserMindMapKnowledge
          user={selectedUserForKnowledge}
          onClose={() => {
            setShowUserKnowledge(false);
            setSelectedUserForKnowledge(null);
          }}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Záloha a obnovení */}
      {showBackupRestore && (
        <BackupRestore
          isDarkMode={isDarkMode}
          companies={companies}
          userId={currentUserId || userId}
          isAdmin={userRole === 'admin'}
          onRestore={onRestore}
          onClose={() => setShowBackupRestore(false)}
        />
      )}

      {/* Role Editor */}
      {showRoleEditor && (
        <RoleEditor
          isDarkMode={isDarkMode}
          onClose={() => setShowRoleEditor(false)}
        />
      )}

      {/* AI Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-4xl rounded-lg shadow-2xl overflow-hidden ${
            isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'
          }`}>
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex justify-between items-center rounded-t-lg">
              <div className="flex items-center space-x-4 flex-wrap gap-2">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">🤖 AI Nastavení</h2>
                <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1 gap-1">
                  <button
                    onClick={() => setAiModalMode('data')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200 ${
                      aiModalMode === 'data'
                        ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-300 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    📊 Data
                  </button>
                  <button
                    onClick={() => setAiModalMode('prompt')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200 ${
                      aiModalMode === 'prompt'
                        ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-300 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    ⚙️ Prompt
                  </button>
                  <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />
                  <button
                    onClick={() => setAiModalMode('offer-data')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200 ${
                      aiModalMode === 'offer-data'
                        ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-300 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    🎯 Co nabídnout? — Data
                  </button>
                  <button
                    onClick={() => setAiModalMode('offer-prompt')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200 ${
                      aiModalMode === 'offer-prompt'
                        ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-300 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    🎯 Co nabídnout? — Prompt
                  </button>
                </div>
              </div>
              <button
                onClick={() => setShowAIModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              {(aiModalMode === 'data' || aiModalMode === 'prompt') && (
                <textarea
                  value={aiModalMode === 'data' ? aiData : aiPrompt}
                  onChange={(e) => aiModalMode === 'data' ? setAiData(e.target.value) : setAiPrompt(e.target.value)}
                  placeholder={
                    aiModalMode === 'data'
                      ? 'Napište zde informace pro AI asistenta (např. fakta o společnosti, produktech, procesech...)'
                      : 'Zadejte systémový prompt pro AI (např. jak má komunikovat, jaký tón používat...)'
                  }
                  className="flex-1 p-4 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white font-mono text-sm w-full"
                  rows={20}
                />
              )}
              {aiModalMode === 'offer-data' && (
                <textarea
                  value={aiOfferData}
                  onChange={(e) => setAiOfferData(e.target.value)}
                  placeholder='Napište zde seznam produktů a služeb, které AI bude doporučovat firmám (např. mobilní tarify, internet, HW...). AI tyto informace použije při generování návrhu "Co nabídnout?".'
                  className="flex-1 p-4 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white font-mono text-sm w-full"
                  rows={20}
                />
              )}
              {aiModalMode === 'offer-prompt' && (
                <textarea
                  value={aiOfferPrompt}
                  onChange={(e) => setAiOfferPrompt(e.target.value)}
                  placeholder='Zadejte instrukce pro AI, jak má zpracovávat návrhy produktů na základě předmětů podnikání firmynapříklad: "Jsi obchodní poradce. Na základě předmětů podnikání navrhni konkrétní produkty..."'
                  className="flex-1 p-4 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white font-mono text-sm w-full"
                  rows={20}
                />
              )}
              <div className="mt-4 flex justify-between items-center">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {aiModalMode === 'data' && '💡 Tip: AI bude tyto informace používat při všech odpovědích uživatelům'}
                  {aiModalMode === 'prompt' && '⚠️ Pozor: Změny promptu ovlivní způsob, jakým AI odpovídá na všechny dotazy'}
                  {aiModalMode === 'offer-data' && '🎯 Produkty a služby, které AI nabídne zákazníkům na základě předmětů podnikání'}
                  {aiModalMode === 'offer-prompt' && '🎯 Instrukce pro AI při generování návrhů "Co nabídnout?" ze zákaznické karty'}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowAIModal(false)}
                    className="px-6 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors duration-300"
                  >
                    Zrušit
                  </button>
                  <button
                    onClick={handleSaveAIData}
                    className={`px-6 py-2 text-white rounded-lg transition-colors duration-300 ${
                      aiModalMode === 'offer-data' || aiModalMode === 'offer-prompt'
                        ? 'bg-teal-600 hover:bg-teal-700'
                        : aiModalMode === 'data'
                          ? 'bg-purple-600 hover:bg-purple-700'
                          : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {aiModalMode === 'data' ? 'Uložit data' : aiModalMode === 'prompt' ? 'Uložit prompt' : aiModalMode === 'offer-data' ? 'Uložit data nabídky' : 'Uložit prompt nabídky'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pro nastavení koeficientu očekávané provize */}
      {showCommissionCoef && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-2xl shadow-2xl p-6 w-full max-w-md ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
            <h2 className="text-xl font-bold mb-4">💰 Koeficient očekávané provize</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Koeficient se násobí hodnotou BMSL. Můžete zadat rozsah (např. 4,75 – 6,25) nebo jednu fixní hodnotu.
            </p>

            {/* Přepínač rozsah / fixní */}
            <div className="flex gap-3 mb-4">
              <button
                onClick={() => setCoefSingle(false)}
                className={`flex-1 py-2 rounded-lg font-semibold text-sm transition ${
                  !coefSingle ? 'bg-blue-600 text-white' : (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700')
                }`}
              >
                Rozsah (od – do)
              </button>
              <button
                onClick={() => setCoefSingle(true)}
                className={`flex-1 py-2 rounded-lg font-semibold text-sm transition ${
                  coefSingle ? 'bg-blue-600 text-white' : (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700')
                }`}
              >
                Fixní hodnota
              </button>
            </div>

            {coefSingle ? (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Koeficient</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={coefMin}
                  onChange={e => setCoefMin(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${
                    isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="např. 5.5"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Příklad: BMSL 100 × {coefMin || '5'} = {Math.round((parseFloat(coefMin) || 5) * 100).toLocaleString('cs-CZ')} Kč
                </p>
              </div>
            ) : (
              <div className="flex gap-3 mb-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">Od (minimum)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={coefMin}
                    onChange={e => setCoefMin(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${
                      isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="např. 4.75"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">Do (maximum)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={coefMax}
                    onChange={e => setCoefMax(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${
                      isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="např. 6.25"
                  />
                </div>
              </div>
            )}

            {!coefSingle && coefMin && coefMax && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Příklad: BMSL 100 → {Math.round((parseFloat(coefMin) || 4) * 100).toLocaleString('cs-CZ')} – {Math.round((parseFloat(coefMax) || 6) * 100).toLocaleString('cs-CZ')} Kč
              </p>
            )}

            <div className="flex gap-3 mt-2">
              <button
                disabled={coefSaving || !coefMin}
                onClick={async () => {
                  setCoefSaving(true);
                  try {
                    const body = {
                      coefMin: parseFloat(coefMin),
                      coefMax: coefSingle ? null : (coefMax ? parseFloat(coefMax) : null)
                    };
                    const res = await fetch(`${API_URL}/commission-coef?userId=${localStorage.getItem('lastUserId') || ''}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(body)
                    });
                    if (res.ok) {
                      const saved = await res.json();
                      setCommissionCoef(saved);
                      setShowCommissionCoef(false);
                      // Ulož do localStorage aby App.js mělo ihned akt. hodnoty
                      localStorage.setItem('commissionCoef', JSON.stringify(saved));
                    } else {
                      alert('Nepodařilo se uložit.');
                    }
                  } catch { alert('Chyba při ukládání.'); }
                  finally { setCoefSaving(false); }
                }}
                className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg font-semibold transition"
              >
                {coefSaving ? 'Ukládám…' : 'Uložit'}
              </button>
              <button
                onClick={() => setShowCommissionCoef(false)}
                className={`flex-1 py-2 rounded-lg font-semibold transition ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal pro nastavení grafů */}
      {showChartSettings && (
        <ChartSettings 
          onClose={() => setShowChartSettings(false)}
          isDarkMode={isDarkMode}
          users={users}
          currentUserId={currentUserId}
        />
      )}
        </div>
      </div>
    </div>
  );
}